import "./App.css";
import { useEffect, useState } from "react";
import MetaphorNetwork from "./MetaphorNetwork";
import LineChart from "./LineChart";
import HeatmapMatrix from "./HeatmapMatrix";
import RandomExamples from "./RandomExamples";
import { loadNetworkData } from "./processData";

function App() {
  // eslint-disable-next-line
  const [data, setData] = useState({ nodes: [], links: [] });
  const [overlapData, setOverlapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const networkData = await loadNetworkData();
        setData(networkData);

        // Extract overlap data from nodes
        const ext = networkData.nodes.filter((n) =>
          n.metaphorTypes.includes("extension-of-self"),
        );
        const tool = networkData.nodes.filter((n) =>
          n.metaphorTypes.includes("magical-tool"),
        );
        const person = networkData.nodes.filter((n) =>
          n.metaphorTypes.includes("person"),
        );

        const extOnly = ext.filter((n) => n.metaphorTypes.length === 1);
        const toolOnly = tool.filter((n) => n.metaphorTypes.length === 1);
        const personOnly = person.filter((n) => n.metaphorTypes.length === 1);

        const extTool = networkData.nodes.filter(
          (n) =>
            n.metaphorTypes.includes("extension-of-self") &&
            n.metaphorTypes.includes("magical-tool") &&
            n.metaphorTypes.length === 2,
        );
        const extPerson = networkData.nodes.filter(
          (n) =>
            n.metaphorTypes.includes("extension-of-self") &&
            n.metaphorTypes.includes("person") &&
            n.metaphorTypes.length === 2,
        );
        const toolPerson = networkData.nodes.filter(
          (n) =>
            n.metaphorTypes.includes("magical-tool") &&
            n.metaphorTypes.includes("person") &&
            n.metaphorTypes.length === 2,
        );
        const all = networkData.nodes.filter(
          (n) => n.metaphorTypes.length === 3,
        );

        setOverlapData({
          extension: extOnly.length,
          tool: toolOnly.length,
          person: personOnly.length,
          extTool: extTool.length,
          extPerson: extPerson.length,
          toolPerson: toolPerson.length,
          all: all.length,
        });
      } catch (err) {
        setError(err.message);
        console.error("Error loading data:", err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  return (
    <div className="App">
      <header className="App-header">
        <h1>AI Metaphors</h1>
        <p className="subtitle">
          A{" "}
          <a
            className="website-links"
            target="_blank"
            rel="noopener noreferrer"
            href="https://sicss.io/2026/uw/"
          >
            SICSS-UW
          </a>{" "}
          project by{" "}
          <a
            className="website-links"
            target="_blank"
            rel="noopener noreferrer"
            href="https://www.meziahruby.space/"
          >
            Meziah Ruby Cristobal
          </a>{" "}
          and{" "}
          <a
            className="website-links"
            target="_blank"
            rel="noopener noreferrer"
            href="https://amywxiao.github.io/"
          >
            Amy Wei Xiao
          </a>
        </p>
      </header>
      <main className="App-main">
        {loading && <p className="status-message">Loading visualizations...</p>}
        {error && <p className="error-message">Error loading data: {error}</p>}
        {!loading && !error && overlapData && (
          <>
            <div className="intro-section">
              <h2>Exploring AI Product Metaphors</h2>
              <p>
                This dashboard analyzes how AI products on Product Hunt are
                metaphorically described using three primary frameworks:
              </p>
              <div className="metaphor-definitions">
                <div className="definition">
                  <strong>🔵 Extension of Self:</strong> AI as an enhancement or
                  amplification of human capabilities
                  <div className="definition-stats">
                    <span className="stat-number">
                      {overlapData.extension +
                        overlapData.extTool +
                        overlapData.extPerson +
                        overlapData.all}
                    </span>
                    <span className="stat-percent">
                      (
                      {(
                        ((overlapData.extension +
                          overlapData.extTool +
                          overlapData.extPerson +
                          overlapData.all) /
                          (overlapData.extension +
                            overlapData.tool +
                            overlapData.person +
                            overlapData.extTool +
                            overlapData.extPerson +
                            overlapData.toolPerson +
                            overlapData.all)) *
                        100
                      ).toFixed(1)}
                      %)
                    </span>
                  </div>
                </div>
                <div className="definition">
                  <strong>🟣 Magical Tool:</strong> AI as a powerful, autonomous
                  tool that feels almost magical
                  <div className="definition-stats">
                    <span className="stat-number">
                      {overlapData.tool +
                        overlapData.extTool +
                        overlapData.toolPerson +
                        overlapData.all}
                    </span>
                    <span className="stat-percent">
                      (
                      {(
                        ((overlapData.tool +
                          overlapData.extTool +
                          overlapData.toolPerson +
                          overlapData.all) /
                          (overlapData.extension +
                            overlapData.tool +
                            overlapData.person +
                            overlapData.extTool +
                            overlapData.extPerson +
                            overlapData.toolPerson +
                            overlapData.all)) *
                        100
                      ).toFixed(1)}
                      %)
                    </span>
                  </div>
                </div>
                <div className="definition">
                  <strong>🟣 Person:</strong> AI as an entity with personality,
                  agency, or human-like qualities
                  <div className="definition-stats">
                    <span className="stat-number">
                      {overlapData.person +
                        overlapData.extPerson +
                        overlapData.toolPerson +
                        overlapData.all}
                    </span>
                    <span className="stat-percent">
                      (
                      {(
                        ((overlapData.person +
                          overlapData.extPerson +
                          overlapData.toolPerson +
                          overlapData.all) /
                          (overlapData.extension +
                            overlapData.tool +
                            overlapData.person +
                            overlapData.extTool +
                            overlapData.extPerson +
                            overlapData.toolPerson +
                            overlapData.all)) *
                        100
                      ).toFixed(1)}
                      %)
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Random Examples Section */}
            <RandomExamples />

            <div className="visualizations-section">
              {/* Metaphor Network */}
              <section className="viz-section">
                <MetaphorNetwork data={overlapData} />
              </section>

              {/* Line Chart */}
              <section className="viz-section">
                <LineChart />
              </section>

              {/* Heatmap Matrix */}
              <section className="viz-section">
                <HeatmapMatrix data={overlapData} />
              </section>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

export default App;
